import { useEffect, useRef, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isPushSupported, currentPermission, subscribePush, sendTestPush } from "@/lib/push";

// Subtle, dismissible nudge that appears above the Matches list when the user
// could plausibly turn on push notifications but hasn't. Hidden entirely if:
//   - Browser doesn't support push (iOS Safari pre-install, incognito, etc.)
//   - Notification permission is "denied" (they already said no — don't nag)
//   - There's already an active subscription
//   - The user dismissed the nudge within the last 7 days
const DISMISS_KEY = "fm_dismissed_notif_nudge_at";
const NAG_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Fire-and-forget funnel tracking so the admin dashboard can compare shown →
// subscribed rates across placements.
const track = (event, source) => {
  try { api.post("/analytics/nudge", { event, source }).catch(() => {}); }
  catch { /* silent */ }
};

export default function EnableNotificationsNudge({ source = "matches", variant = "default" }) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const shownFiredRef = useRef(false);

  // Copy variants keyed by placement context. `matchday` is a higher-intent
  // pitch shown on the Fixtures page during a live match window.
  const copy = variant === "matchday"
    ? {
        title: "Get pinged when a fan says hi",
        body: "Match is on — get a lock-screen nudge the second someone at your ground taps to meet.",
      }
    : {
        title: "Never miss a Footymatch",
        body: "Get a ping the second someone matches with you — even when the app's closed.",
      };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isPushSupported()) return;
      if (currentPermission() === "denied") return;
      // Respect the 7-day snooze
      try {
        const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
        if (dismissedAt && Date.now() - dismissedAt < NAG_INTERVAL_MS) return;
      } catch { /* ignore */ }
      // Check for an existing subscription — hide if already subscribed
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) return;
        }
      } catch { /* ignore — treat as no sub */ }
      if (!cancelled) setVisible(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Fire the "shown" event exactly once per mount, per placement — after the
  // visibility gates have passed.
  useEffect(() => {
    if (visible && !shownFiredRef.current) {
      shownFiredRef.current = true;
      track("shown", source);
    }
  }, [visible, source]);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      toast.success("Notifications on — we'll ping you the moment you match.");
      setVisible(false);
      track("subscribed", source);
      // Fire a confirmation push so the user sees it working on their lock screen —
      // proves the pipe is live and removes the "did that actually turn on?" anxiety.
      // Fire-and-forget; failures are silent (subscription itself already succeeded).
      sendTestPush().catch(() => { /* silent */ });
    } catch (e) {
      if (e?.code === "denied") {
        toast.error("Notifications blocked. Turn them back on in your browser settings.");
        setVisible(false);
      } else {
        toast.error(e?.message || "Could not turn on notifications");
      }
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
    track("dismissed", source);
  };

  if (!visible) return null;

  return (
    <div
      className="relative footy-card p-4 mb-5 border-footy-red/40 bg-footy-red/[0.06] flex items-start gap-3"
      data-testid="enable-notifications-nudge"
    >
      <div className="w-10 h-10 rounded-xl bg-footy-red/20 text-footy-red flex items-center justify-center flex-none">
        <Bell size={18} />
      </div>
      <div className="flex-1 min-w-0 pr-6">
        <div className="font-bold leading-tight">{copy.title}</div>
        <p className="text-xs text-slate-400 mt-1 leading-relaxed">
          {copy.body}
        </p>
        <button
          onClick={enable}
          disabled={busy}
          className="mt-3 footy-btn-primary !py-2 !px-4 !text-xs disabled:opacity-50"
          data-testid="enable-notifications-btn"
        >
          <Bell size={12} /> {busy ? "Turning on…" : "Turn on notifications"}
        </button>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-slate-300 flex items-center justify-center"
        data-testid="enable-notifications-dismiss"
      >
        <X size={13} />
      </button>
    </div>
  );
}
